import { type CSSProperties, forwardRef, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { Button } from '@unifyapps/ui/components/Button';
import { Popover } from '@unifyapps/ui/components/Popover';
import { MenuList } from '@unifyapps/ui/components/Menu';
import type { MenuListItem } from '@unifyapps/ui/components/Menu/MenuList';
import type { ButtonGroupBlockStateType, Option } from '@unifyapps/defs/blocks/ButtonGroup/types';
import { useTranslation } from '@unifyapps/i18n/client';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { getConditionalIcon } from '@unifyapps/carbon/utils/conditionalValues';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import type { StyleObject } from '@unifyapps/defs/types/styleObject';
import type { UpdateBlockStateType } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
} from '@unifyapps/defs/types/blocks/button';

type Props = {
  attributesAndEventListeners: Record<string, unknown> | undefined;
  text: string | undefined;
  options: Option[];
  onAction: (id: string, label: string) => void;
  className?: string;
  style?: CSSProperties;
  color?: ButtonColors;
  size?: ButtonSizes;
  variant?: ButtonVariants;
  startDecorator?: string;
  endDecorator?: string;
  popupAppearance?: {
    styles?: Pick<StyleObject, 'maxHeight' | 'overflow'>;
  };
  isOpen?: boolean;
  updateBlockState: UpdateBlockStateType<ButtonGroupBlockStateType>;
};

const ICON_ONLY_STYLES = { '--button-padding-x': 'var(--button-padding-y)' } as React.CSSProperties;

export const DropdownButtonGroup = forwardRef<HTMLButtonElement, Props>(
  function DropdownButtonGroup(props, ref) {
    const {
      className,
      attributesAndEventListeners,
      text,
      options,
      onAction,
      color,
      size,
      variant,
      startDecorator,
      endDecorator,
      popupAppearance,
      isOpen,
      updateBlockState,
    } = props;

    const { t } = useTranslation(['common']);

    const onOpenChange = useCallback(
      (open: boolean) => {
        updateBlockState((draft) => {
          draft.isOpen = open;
        });
      },
      [updateBlockState],
    );

    const handleMenuClick = useCallback(
      (id: string) => {
        onAction(id, options.find((option) => option.id === id)?.label ?? '');
        onOpenChange(false);
      },
      [onAction, onOpenChange, options],
    );

    const items = useMemo(
      () =>
        options.map(
          (option) =>
            ({
              id: option.id,
              title: option.appearance?.loading ? `${t('common:Loading')}...` : option.label,
              type: 'label' as const,
              variant: option.appearance?.variant,
              disabled: option.appearance?.disabled,
              endDecoratorComponent: getIconFromRegistry(option.appearance?.endDecorator),
              startDecoratorComponent: getIconFromRegistry(option.appearance?.startDecorator),
            }) as MenuListItem,
        ),
      [options, t],
    );

    const { className: popupClassName, style: popupStyle } = useBlockAppearanceStyles({
      appearanceStyles: popupAppearance?.styles ?? {},
    });

    const endDecoratorComponent = getConditionalIcon(endDecorator);
    const startDecoratorComponent = getConditionalIcon(startDecorator);
    // Check if the button only has an icon (no text)
    // This is true when there's no text content AND exactly one decorator (either start or end, but not both)
    const hasOnlyIcon =
      !text?.trim() && Boolean(startDecoratorComponent) !== Boolean(endDecoratorComponent);

    return (
      <Popover onOpenChange={onOpenChange} open={isOpen}>
        <Popover.Button>
          <Button
            className={clsx({ '!min-w-[unset]': hasOnlyIcon }, className)}
            color={color}
            endDecoratorComponent={endDecoratorComponent}
            ref={ref}
            size={size}
            startDecoratorComponent={startDecoratorComponent}
            style={hasOnlyIcon ? ICON_ONLY_STYLES : undefined}
            variant={variant}
            {...attributesAndEventListeners}
          >
            {text}
          </Button>
        </Popover.Button>
        <Popover.Content useAnchorElWidth={!hasOnlyIcon}>
          <MenuList
            className={popupClassName}
            items={items}
            onAction={handleMenuClick}
            style={popupStyle}
          />
        </Popover.Content>
      </Popover>
    );
  },
);
